import { z } from "zod";

import type { DbEntity } from "../../../../client";
import { DocumentResponseSchema } from "./responses";

/**
 * Folder-specific operations
 * Includes: CreateFolder, GetSubfolders, GetFolderContents, GetFolderPath
 */

// ============================================================================
// CREATE FOLDER
// ============================================================================

export const DocumentCreateFolderBodySchema = z.object({
  name: z.string().trim().min(1).max(255),
  parentId: z.string().optional(),
  description: z.string().optional()
});

// ============================================================================
// GET SUBFOLDERS
// ============================================================================

export const DocumentGetSubfoldersParamsSchema = z.object({
  parentId: z.string().nullable().optional()
});

/**
 * One subfolder, as the subfolder listing actually reads it.
 *
 * The backend selects exactly these six columns — a folder tree needs a label,
 * a parent link and a child count, and nothing else. Declaring the whole
 * `Document` here let a consumer read `folder.size`, compile, and get
 * `undefined` at runtime.
 *
 * The `satisfies` is the half of the contract a Zod schema cannot state on its
 * own: it pins every field to the column it projects, so renaming a column or
 * changing its nullability in `schema.prisma` fails here rather than at the
 * consumer that reads the wrong thing.
 */
export const DocumentSubfolderSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  parentId: z.string().nullable(),
  isFolder: z.boolean(),
  totalChildren: z.number().int()
}) satisfies z.ZodType<
  Pick<DbEntity.Document, "id" | "name" | "displayName" | "parentId" | "isFolder" | "totalChildren">
>;

export type DocumentSubfolderSummary = z.infer<typeof DocumentSubfolderSummarySchema>;

export const DocumentGetSubfoldersResponseSchema = z.array(DocumentSubfolderSummarySchema);

// ============================================================================
// GET FOLDER CONTENTS
// ============================================================================

export const DocumentGetFolderContentsPathVarsSchema = z.object({
  id: z.string()
});

export const DocumentGetFolderContentsResponseSchema = z.array(DocumentResponseSchema);

// ============================================================================
// GET FOLDER PATH
// ============================================================================

export const DocumentGetFolderPathPathVarsSchema = z.object({
  id: z.string()
});

/**
 * One entry of a folder breadcrumb trail.
 *
 * The backend selects exactly these seven columns, deliberately: routing the
 * walk through the full-document read would attach every entry's `parent`,
 * `children` and `organization` rows to a response that renders a chain of
 * names, so a breadcrumb for a folder holding a thousand documents would ship
 * all thousand.
 *
 * The narrow read is right; declaring `Document` over it was the defect. It let
 * a consumer write `trail[0].content`, compile cleanly, and read `undefined`.
 */
export const DocumentBreadcrumbEntrySchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string().nullable(),
  parentId: z.string().nullable(),
  isFolder: z.boolean(),
  path: z.string(),
  organizationId: z.string()
}) satisfies z.ZodType<
  Pick<
    DbEntity.Document,
    "id" | "name" | "displayName" | "parentId" | "isFolder" | "path" | "organizationId"
  >
>;

export type DocumentBreadcrumbEntry = z.infer<typeof DocumentBreadcrumbEntrySchema>;

export const DocumentGetFolderPathResponseSchema = z.array(DocumentBreadcrumbEntrySchema);
